import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { jsPDF } from 'jspdf';
import { formatInvoiceAmount } from '../pricing/actPricingUtils';

const HEADER_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: '0019FF' } };
const HEADER_FONT = { bold: true, color: { argb: 'FFFFFF' }, size: 10 };
const TITLE_FONT = { bold: true, size: 14, color: { argb: '0F172A' } };
const SECTION_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'BFDBFE' } };
const GRAND_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: '0019FF' } };
const GRAND_FONT = { bold: true, color: { argb: 'FFFFFF' }, size: 10 };
const THIN_BORDER = {
  top: { style: 'thin', color: { argb: '0F172A' } },
  bottom: { style: 'thin', color: { argb: '0F172A' } },
  left: { style: 'thin', color: { argb: '0F172A' } },
  right: { style: 'thin', color: { argb: '0F172A' } }
};

const COL_COUNT = 8;
const MONEY_FMT = '#,##0.00';
const HEADERS = [
  'Date',
  'Nom',
  'Matricule',
  'Société',
  'Désignation',
  'Quantité',
  'Prix Unitaire',
  'Montant Total'
];

function formatDateOnly(value) {
  if (!value) return '';
  try {
    return new Date(value).toLocaleDateString('fr-FR');
  } catch {
    return '';
  }
}

function formatDateTime(value) {
  if (!value) return '';
  try {
    return new Date(value).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return '';
  }
}

function stamp() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}_${p(d.getHours())}${p(d.getMinutes())}`;
}

function formatQty(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  if (Math.abs(n - Math.round(n)) < 0.001) return String(Math.round(n));
  return n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function isCoverageInvoice(inv) {
  return String(inv?.invoice_kind || '').toUpperCase() === 'COVERAGE';
}

function sectionLabel(inv) {
  return isCoverageInvoice(inv) ? 'PRISES EN CHARGE' : 'SOINS ET TRAITEMENT';
}

function exportTitle(invoices = []) {
  const kinds = new Set(invoices.map((inv) => String(inv.invoice_kind || 'CARE').toUpperCase()));
  if (kinds.size === 1 && kinds.has('COVERAGE')) return 'FACTURATION — PRISES EN CHARGE';
  if (kinds.size === 1) return 'FACTURATION — SOINS ET TRAITEMENT';
  return 'FACTURATION — FUNTOA SMIE';
}

function exportFilenamePrefix(invoices = []) {
  const kinds = new Set(invoices.map((inv) => String(inv.invoice_kind || 'CARE').toUpperCase()));
  if (kinds.size === 1 && kinds.has('COVERAGE')) return 'factures_prises_en_charge';
  if (kinds.size === 1) return 'factures_soins';
  return 'factures_SMIE';
}

function invoiceItems(inv) {
  if (Array.isArray(inv.items) && inv.items.length) return inv.items;
  return [
    {
      label: '—',
      quantity: 1,
      unit_price: inv.total_amount || 0,
      line_total: inv.total_amount || 0
    }
  ];
}

function pushItemRow(rows, inv, item, first) {
  rows.push({
    type: 'item',
    date: first ? formatDateOnly(inv.service_date || inv.created_at) : '',
    fullname: first ? inv.patient_name || '' : '',
    matricule: first ? inv.member_matricule || '' : '',
    company: first ? inv.company_name || '' : '',
    designation: item.label || '',
    quantity: Number(item.quantity ?? 1),
    unitPrice: Number(item.unit_price ?? 0),
    amount: Number(item.line_total || 0)
  });
}

function pushSectionRow(rows, designation, amount) {
  rows.push({
    type: 'subtotal',
    date: '',
    fullname: '',
    matricule: '',
    company: '',
    designation,
    quantity: '',
    unitPrice: '-',
    amount: Number(amount || 0)
  });
}

function buildExportRows(invoices = []) {
  const rows = [];

  invoices.forEach((inv) => {
    const items = invoiceItems(inv);
    items.forEach((item, index) => {
      pushItemRow(rows, inv, item, index === 0);
    });
    pushSectionRow(rows, sectionLabel(inv), inv.total_amount);
  });

  const grand = invoices.reduce((sum, inv) => sum + Number(inv.total_amount || 0), 0);
  rows.push({
    type: 'grand',
    date: '',
    fullname: '',
    matricule: '',
    company: '',
    designation: 'TOTAL GÉNÉRAL',
    quantity: '',
    unitPrice: '-',
    amount: grand
  });

  return rows;
}

function applyExcelItemRow(row) {
  row.eachCell((cell) => {
    cell.border = THIN_BORDER;
    cell.alignment = { vertical: 'middle' };
  });
  [1, 2, 3, 4, 5].forEach((i) => {
    row.getCell(i).alignment = { vertical: 'middle', horizontal: 'left' };
  });
  [6, 7, 8].forEach((i) => {
    row.getCell(i).alignment = { vertical: 'middle', horizontal: 'right' };
  });
}

export async function exportInvoicesToExcel(invoices = []) {
  const wb = new ExcelJS.Workbook();
  wb.creator = 'FUNTOA SMIE';
  wb.created = new Date();

  const ws = wb.addWorksheet('Factures');
  ws.columns = [
    { width: 12 },
    { width: 28 },
    { width: 14 },
    { width: 18 },
    { width: 42 },
    { width: 12 },
    { width: 16 },
    { width: 16 }
  ];

  ws.mergeCells(1, 1, 1, COL_COUNT);
  ws.getCell(1, 1).value = exportTitle(invoices);
  ws.getCell(1, 1).font = TITLE_FONT;
  ws.getRow(1).height = 24;

  ws.mergeCells(2, 1, 2, COL_COUNT);
  ws.getCell(2, 1).value = `Export du ${formatDateTime(new Date())} · ${invoices.length} facture(s)`;
  ws.getCell(2, 1).font = { size: 10, color: { argb: '64748B' } };

  const header = ws.getRow(4);
  header.values = HEADERS;
  header.height = 22;
  header.eachCell((cell) => {
    cell.fill = HEADER_FILL;
    cell.font = HEADER_FONT;
    cell.border = THIN_BORDER;
    cell.alignment = { vertical: 'middle', horizontal: 'center' };
  });

  const rows = buildExportRows(invoices);
  const subtotalExcelRows = [];
  let groupStart = null;

  rows.forEach((line) => {
    const excelRow = ws.addRow([
      line.date,
      line.fullname,
      line.matricule,
      line.company,
      line.designation,
      line.type === 'item' ? line.quantity : '',
      line.type === 'item' ? line.unitPrice : '-',
      null
    ]);
    const r = excelRow.number;
    applyExcelItemRow(excelRow);
    excelRow.getCell(8).numFmt = MONEY_FMT;

    if (line.type === 'item') {
      if (groupStart == null) groupStart = r;
      const qty = Number(line.quantity);
      excelRow.getCell(6).numFmt =
        Number.isFinite(qty) && Math.abs(qty - Math.round(qty)) < 0.001 ? '0' : '0.00';
      excelRow.getCell(7).numFmt = MONEY_FMT;
      excelRow.getCell(8).value = {
        formula: `F${r}*G${r}`,
        result: Number(line.amount || 0)
      };
      return;
    }

    excelRow.font = { bold: true };

    if (line.type === 'subtotal') {
      const from = groupStart ?? r;
      const to = r - 1;
      excelRow.getCell(8).value =
        to >= from
          ? { formula: `SUM(H${from}:H${to})`, result: Number(line.amount || 0) }
          : 0;
      excelRow.eachCell((cell) => {
        cell.fill = SECTION_FILL;
        cell.border = THIN_BORDER;
      });
      subtotalExcelRows.push(r);
      groupStart = null;
      return;
    }

    excelRow.getCell(8).value = subtotalExcelRows.length
      ? {
          formula:
            subtotalExcelRows.length === 1
              ? `H${subtotalExcelRows[0]}`
              : `SUM(${subtotalExcelRows.map((n) => `H${n}`).join(',')})`,
          result: Number(line.amount || 0)
        }
      : 0;
    excelRow.eachCell((cell) => {
      cell.fill = GRAND_FILL;
      cell.font = GRAND_FONT;
      cell.border = THIN_BORDER;
    });
  });

  const buffer = await wb.xlsx.writeBuffer();
  saveAs(new Blob([buffer]), `${exportFilenamePrefix(invoices)}_${stamp()}.xlsx`);
}

function drawCell(
  doc,
  x,
  y,
  w,
  h,
  text,
  { align = 'left', bold = false, fill = null, fontSize = 8, textColor = [15, 23, 42] } = {}
) {
  doc.setDrawColor(15, 23, 42);
  if (fill) {
    doc.setFillColor(...fill);
    doc.rect(x, y, w, h, 'FD');
  } else {
    doc.rect(x, y, w, h);
  }
  doc.setFont('helvetica', bold ? 'bold' : 'normal');
  doc.setFontSize(fontSize);
  doc.setTextColor(...textColor);
  const pad = 1.4;
  const clipped = doc.splitTextToSize(String(text ?? ''), w - pad * 2)[0] || '';
  const textY = y + h / 2 + 1.1;
  if (align === 'right') doc.text(clipped, x + w - pad, textY, { align: 'right' });
  else doc.text(clipped, x + pad, textY);
}

function pdfCellValue(line, key) {
  if (key === 'quantity') {
    if (line.type !== 'item') return '';
    return formatQty(line.quantity);
  }
  if (key === 'unitPrice') {
    if (line.type !== 'item') return '-';
    return formatInvoiceAmount(line.unitPrice);
  }
  if (key === 'amount') return formatInvoiceAmount(line.amount);
  return line[key] ?? '';
}

export function exportInvoicesToPdf(invoices = []) {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 10;
  const tableW = pageW - margin * 2;
  const cols = [
    { key: 'date', label: 'Date', w: tableW * 0.09 },
    { key: 'fullname', label: 'Nom', w: tableW * 0.16 },
    { key: 'matricule', label: 'Matricule', w: tableW * 0.1 },
    { key: 'company', label: 'Société', w: tableW * 0.11 },
    { key: 'designation', label: 'Désignation', w: tableW * 0.22 },
    { key: 'quantity', label: 'Quantité', w: tableW * 0.08 },
    { key: 'unitPrice', label: 'Prix Unitaire', w: tableW * 0.12 },
    { key: 'amount', label: 'Montant Total', w: tableW * 0.12 }
  ];
  const rowH = 7.5;
  const rows = buildExportRows(invoices);

  const drawHeaderBand = (y) => {
    let x = margin;
    cols.forEach((col) => {
      doc.setFillColor(0, 25, 255);
      doc.setDrawColor(15, 23, 42);
      doc.rect(x, y, col.w, rowH, 'FD');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.setTextColor(255, 255, 255);
      doc.text(col.label, x + 1.4, y + 4.9);
      x += col.w;
    });
  };

  let y = 12;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(15, 23, 42);
  doc.text(`FUNTOA SMIE — ${exportTitle(invoices).replace('FACTURATION — ', '')}`, margin, y);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(100, 116, 139);
  doc.text(`Export du ${formatDateTime(new Date())}`, pageW - margin, y, { align: 'right' });
  y += 8;
  drawHeaderBand(y);
  y += rowH;

  if (!invoices.length) {
    doc.setFontSize(10);
    doc.setTextColor(15, 23, 42);
    doc.text('Aucune facture à exporter.', margin, y + 8);
    doc.save(`${exportFilenamePrefix(invoices)}_${stamp()}.pdf`);
    return;
  }

  rows.forEach((line) => {
    if (y + rowH > pageH - 12) {
      doc.addPage();
      y = 12;
      drawHeaderBand(y);
      y += rowH;
    }

    const isRight = (key) => key === 'quantity' || key === 'unitPrice' || key === 'amount';
    let x = margin;
    cols.forEach((col) => {
      const opts = {
        align: isRight(col.key) ? 'right' : 'left',
        fontSize: 8
      };
      if (line.type === 'subtotal') {
        opts.bold = true;
        opts.fill = [191, 219, 254];
      } else if (line.type === 'grand') {
        opts.bold = true;
        opts.fill = [0, 25, 255];
        opts.textColor = [255, 255, 255];
      }
      drawCell(doc, x, y, col.w, rowH, pdfCellValue(line, col.key), opts);
      x += col.w;
    });
    y += rowH;
  });

  doc.save(`${exportFilenamePrefix(invoices)}_${stamp()}.pdf`);
}
